import { useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Loader2 } from 'lucide-react';
import { useAuth } from '@core/auth/AuthProvider';
import { redirectIfProfileIncomplete } from '@core/auth';
import { useBooking } from '@features/booking/state';
import { useBookingFlow } from '@features/booking/flow';
import type { BookingReturnParams } from '@core/auth/authReturn';

/**
 * AuthReturnPage — Phase 2 of auth return flow.
 *
 * Route: /auth-return-complete[?ps=4&d=2025-03-05&st=dinner&t=19:30]
 *
 * At this point the user is already authenticated (AuthReturnGate handled
 * the token). This page reconstructs booking state and navigates to the
 * correct step. It lives inside TenantGuard so it has access to booking
 * and restaurant context.
 */
export function AuthReturnPage() {
  const navigate = useNavigate();
  const { t } = useTranslation('auth');
  const [searchParams] = useSearchParams();
  const { authState } = useAuth();
  const booking = useBooking();
  const { activeSteps } = useBookingFlow();
  const processedRef = useRef(false);

  const [ready, setReady] = useState(false);
  const [target, setTarget] = useState<'booking' | 'reconstruct' | 'home'>('home');
  const [bookingParams, setBookingParams] = useState<BookingReturnParams | null>(null);

  // Phase 1: reconstruct booking state from URL params
  useEffect(() => {
    if (processedRef.current) return;
    processedRef.current = true;

    if (!authState.isAuthenticated) {
      navigate('/', { replace: true });
      return;
    }

    const params = extractBookingParams(searchParams);

    if (params) {
      const updates: Record<string, unknown> = {};
      if (params.partySize) updates.diners = params.partySize;
      if (params.date) updates.selectedDate = params.date;
      if (params.serviceType) updates.service = params.serviceType;
      if (params.time) updates.selectedTime = params.time;
      if (params.flowType) updates.flowType = params.flowType;
      if (params.isEarlyAccess) updates.isEarlyAccessFlow = true;
      booking.updateBooking(updates);
      setBookingParams(params);
      setTarget('reconstruct');
    } else {
      setTarget('booking');
    }

    setReady(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Phase 2: navigate AFTER activeSteps are fresh
  useEffect(() => {
    if (!ready || !authState.isAuthenticated) return;

    // Compute the natural post-auth destination first so the profile gate
    // can pass it as `from` and the user resumes the original booking flow
    // after completing their profile (legacy parity with home.js:163).
    const resolvePostAuthPath = (): string => {
      if (bookingParams?.isWaitingList) return '/waiting-list';

      if (target === 'booking' || (target === 'reconstruct' && bookingParams?.time)) {
        if (!booking.timeSlots) return '/select-time';
        // Skip auth-related steps (SIGN_IN, COMPLETE_PROFILE) when locating
        // the next step. The profile completion gate is handled below; if it
        // fires it will use this resolved path as `from` so the user resumes
        // the actual booking flow after saving — not the auth detour.
        const timeIdx = activeSteps.findIndex(s => s.id === 'TIME');
        const nextStep = activeSteps
          .slice(timeIdx + 1)
          .find(s => s.id !== 'SIGN_IN' && s.id !== 'COMPLETE_PROFILE');
        return nextStep?.path || '/personalize';
      }

      if (target === 'reconstruct') {
        if (bookingParams?.date && bookingParams?.serviceType) return '/select-time';
        if (bookingParams?.partySize) return '/select-date';
      }

      return '/';
    };

    const postAuthPath = resolvePostAuthPath();

    // Profile completion gate: short-circuit before navigating to the
    // booking flow when the server returned an empty name or phone. The
    // ProfileCompletionEnforcer would catch it on the next route change,
    // but doing it here avoids a visible flicker through the target route.
    if (redirectIfProfileIncomplete(navigate, authState, postAuthPath)) return;

    navigate(postAuthPath, { replace: true });
  }, [ready, authState, activeSteps, target, bookingParams, booking.timeSlots, navigate]);

  return (
    <div className="min-h-screen flex flex-col items-center justify-center p-4">
      <Loader2 className="w-8 h-8 animate-spin text-brand mb-4" />
      <p className="text-foreground/50 text-sm">{t('MAGIC_LINK_VERIFY_BUTTON')}</p>
    </div>
  );
}

function extractBookingParams(searchParams: URLSearchParams): BookingReturnParams | null {
  const ps = searchParams.get('ps');
  const d = searchParams.get('d');
  const st = searchParams.get('st');
  const t = searchParams.get('t');
  const ft = searchParams.get('ft');
  const evt = searchParams.get('evt');
  const wl = searchParams.get('wl');
  const ea = searchParams.get('ea');

  if (!ps && !d && !st && !t && !ft && !evt && !wl && !ea) return null;

  return {
    partySize: ps ? Number(ps) : undefined,
    date: d || undefined,
    serviceType: st || undefined,
    time: t || undefined,
    flowType: ft || undefined,
    eventSlug: evt || undefined,
    isWaitingList: wl === '1',
    isEarlyAccess: ea === '1',
  };
}

export default AuthReturnPage;
